/* assets/section-nutrition-hotspots.js */
(function () {
  const NS = 'aloha-hotspots';
  const BP = 990; // breakpoint
  const DOT = 48, DOT_HALF = DOT / 2;

  // Utility: retry until condition passes
  function waitFor(conditionFn, cb, { tries = 40, interval = 75 } = {}) {
    let n = 0;
    const tick = () => {
      if (conditionFn()) return cb();
      if (++n >= tries) return;
      setTimeout(tick, interval);
    };
    tick();
  }

  class HotspotsController {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector(`.${NS}__stage`);
      this.splide = null;
      this.resizeObs = null;
      this.mq = window.matchMedia(`(max-width: ${BP}px)`);
      this.boundResize = this.debounce(this.layout.bind(this), 120);
      this.boundClickAway = this.clickAway.bind(this);
      this.boundMQ = this.onMQChange.bind(this);
    }

    init() {
      if (!this.stage) return;

      this.wireA11y();

      // Mount Splide right away if already mobile
      if (this.mq.matches) this.mountSplide();

      // Layout desktop after images render
      this.onImagesReady(() => {
        this.layout();
        if (this.mq.matches) this.mountSplide();
      });

      if ('ResizeObserver' in window) {
        this.resizeObs = new ResizeObserver(() => this.layout());
        this.resizeObs.observe(this.stage);
      }

      this.mq.addEventListener
        ? this.mq.addEventListener('change', this.boundMQ)
        : this.mq.addListener(this.boundMQ);

      window.addEventListener('resize', this.boundResize);
      document.addEventListener('click', this.boundClickAway, true);
    }

    destroy() {
      document.removeEventListener('click', this.boundClickAway, true);
      window.removeEventListener('resize', this.boundResize);
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.boundMQ);
      else this.mq.removeListener(this.boundMQ);
      if (this.resizeObs) { try { this.resizeObs.disconnect(); } catch(_){} }
      this.destroySplide();
      this.root.querySelectorAll(`.${NS}__img, .${NS}__dot`).forEach(el => {
        el.style.left = ''; el.style.top = ''; el.style.transform = '';
      });
    }

    // --- utils ---
    readCssPx(varName, fb = 0) {
      const raw = getComputedStyle(this.root).getPropertyValue(varName).trim();
      const n = parseFloat(raw);
      return isNaN(n) ? fb : n;
    }
    readPercent(el, dataKey, cssVar, fb = 50) {
      let v = el.dataset[dataKey];
      if (v == null || v === '') {
        const raw = getComputedStyle(el).getPropertyValue(cssVar).trim();
        v = raw.endsWith('%') ? raw.slice(0, -1) : raw;
      }
      let n = parseFloat(v);
      if (isNaN(n)) n = fb;
      return Math.max(0, Math.min(100, n));
    }
    clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
    debounce(fn, ms){ let t; return ()=>{ clearTimeout(t); t = setTimeout(fn, ms); }; }

    // --- a11y ---
    wireA11y() {
      this.root.querySelectorAll(`.${NS}__dot`).forEach(btn => {
        if (btn.__wired) return;
        btn.__wired = true;

        const pop = btn.querySelector(`.${NS}__popover`);
        if (!pop) return;
        const id = `popover-${Math.random().toString(36).slice(2)}`;
        btn.setAttribute('aria-controls', id);
        btn.setAttribute('aria-expanded', 'false');
        pop.id = id; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-hidden', 'true');
        const title = pop.querySelector(`.${NS}__popover-title`);
        if (title) { const lid = id + '-title'; title.id = lid; pop.setAttribute('aria-labelledby', lid); }

        btn.addEventListener('click', () => this.toggleDot(btn));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { e.stopPropagation(); this.closeDot(btn, true); }
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const list = Array.from(this.root.querySelectorAll(`.${NS}__dot:not(.${NS}__dot--mobile)`));
            if (!list.length) return;
            const i = list.indexOf(btn);
            (list[(i + (e.key === 'ArrowRight' ? 1 : -1) + list.length) % list.length] || {}).focus?.();
          }
        });
      });
    }
    clickAway(e){ if (!this.root.contains(e.target)) this.closeAllDots(); }
    toggleDot(btn){ const open = btn.getAttribute('aria-expanded') === 'true'; this.closeAllDots(); if (!open) this.openDot(btn); }
    openDot(btn){
      const pop = document.getElementById(btn.getAttribute('aria-controls'));
      btn.setAttribute('aria-expanded', 'true'); pop?.setAttribute('aria-hidden', 'false');
      (pop?.querySelector(`.${NS}__popover-title`) || pop)?.focus?.();
    }
    closeDot(btn, focusBack=false){
      const pop = document.getElementById(btn.getAttribute('aria-controls'));
      btn.setAttribute('aria-expanded', 'false'); pop?.setAttribute('aria-hidden', 'true');
      if (focusBack) btn.focus();
    }
    closeAllDots(){ this.root.querySelectorAll(`.${NS}__dot[aria-expanded="true"]`).forEach(b=>this.closeDot(b)); }

    // --- layout ---
    layout() {
      if (!this.stage || this.mq.matches) return;

      const padX = this.readCssPx('--edge-pad', 40);
      const padY = this.readCssPx('--edge-pad-y', 0);
      const rect = this.stage.getBoundingClientRect();
      const W = rect.width, H = rect.height;

      this.root.querySelectorAll(`.${NS}__img`).forEach(wrap => {
        const xPct = this.readPercent(wrap, 'x', '--x', 50);
        const yPct = this.readPercent(wrap, 'y', '--y', 50);
        const img = wrap.querySelector('img');
        const r = img ? img.getBoundingClientRect() : { width:0, height:0 };

        const cx = this.clamp((W * xPct)/100, padX + r.width/2, W - (padX + r.width/2));
        const topDesired = (H * yPct)/100;
        const topMax = Math.max(0, H - r.height);
        const topPx = this.clamp(topDesired, 0, topMax);

        wrap.style.left = cx + 'px';
        wrap.style.top  = topPx + 'px';
        wrap.style.transform = 'translate(-50%, 0)';
      });

      this.root.querySelectorAll(`.${NS}__dot:not(.${NS}__dot--mobile)`).forEach(dot => {
        const hx = this.readPercent(dot, 'hx', '--hx', 50);
        const hy = this.readPercent(dot, 'hy', '--hy', 50);

        const cx = this.clamp((W * hx)/100, padX + DOT_HALF, W - (padX + DOT_HALF));
        const cy = this.clamp((H * hy)/100, padY + DOT_HALF, H - (padY + DOT_HALF));

        dot.style.left = cx + 'px';
        dot.style.top  = cy + 'px';
        dot.style.transform = 'translate(-50%, -50%)';
      });
    }

    // --- images ---
    onImagesReady(cb){
      const imgs = Array.from(this.root.querySelectorAll(`.${NS}__img img`));
      if (!imgs.length) { cb(); return; }
      let left = imgs.length;
      const done = () => { if (--left <= 0) cb(); };
      imgs.forEach(img => (img.complete ? done() : img.addEventListener('load', done, { once: true })));
    }

    // --- Splide ---
    mountSplide(){
      const el = this.root.querySelector(`.${NS}__carousel`);
      if (!el || el.__splideMounted) return;

      const doMount = () => {
        if (!window.Splide || el.__splideMounted) return;
        this.splide = new Splide(el, {
          type:'loop',
          perPage:1,
          gap:'16px',
          pagination:true,
          arrows:false,
          drag:true
        });
        this.splide.mount();
        el.__splideMounted = true;
      };

      if (window.Splide) doMount();
      else waitFor(() => !!window.Splide, doMount, { tries: 60, interval: 50 });
    }
    destroySplide(){
      const el = this.root.querySelector(`.${NS}__carousel`);
      if (this.splide && el?.__splideMounted) {
        this.splide.destroy(true);
        el.__splideMounted = false;
        this.splide = null;
      }
    }
    onMQChange(){
      if (this.mq.matches) { this.destroySplide(); this.mountSplide(); }
      else { this.destroySplide(); this.layout(); }
    }
  }

  // --- Registry ---
  const REG = new Map();
  function mount(el){
    if (!el || REG.has(el)) return;
    const ctl = new HotspotsController(el);
    REG.set(el, ctl); ctl.init();
  }
  function unmount(el){
    const ctl = REG.get(el); if (!ctl) return;
    ctl.destroy(); REG.delete(el);
  }
  function scan(){ document.querySelectorAll('[data-section-type="nutrition-hotspots"]').forEach(mount); }

  // Boot
  document.addEventListener('DOMContentLoaded', scan);

  // Shopify editor events
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target?.querySelector?.('[data-section-type="nutrition-hotspots"]');
    if (el) mount(el);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target?.querySelector?.('[data-section-type="nutrition-hotspots"]');
    if (el) unmount(el);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const host = e.target?.closest?.('[data-section-type="nutrition-hotspots"]');
    if (host) REG.get(host)?.layout();
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const host = e.target?.closest?.('[data-section-type="nutrition-hotspots"]');
    if (host) REG.get(host)?.layout();
  });

  // Fallback observer
  const mo = new MutationObserver(() => scan());
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
